// Commissions → Settings: the open policy questions Finance answers. Each one
// says exactly what it blocks until it's set — nothing is guessed.
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { errReason } from "@/lib/compPlans";
import { Button } from "@/components/ui/Button";
import { INPUT, Loading, Notice, Pill, Section, TEXTAREA } from "./parts";

interface SettingRow {
  key: string;
  label: string;
  blocks: string;
  input?: "date" | "choice" | "text";
  choices?: Array<{ value: string; label: string }>;
  example?: string;
  value: unknown;
}

/** A stored value as words — never raw JSON on screen. */
function valueText(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return String(v);
  return "";
}

function isSet(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  return true;
}

export function SettingsTab() {
  const q = useQuery({
    queryKey: ["commissions", "plan-settings"],
    queryFn: () => api<{ settings: SettingRow[] }>("/commissions/plan-settings"),
  });
  if (q.isError) return <Notice tone="risk">{errReason(q.error)}</Notice>;
  if (!q.data) return <Loading />;
  const open = q.data.settings.filter((s) => !isSet(s.value)).length;
  return (
    <Section
      title="Settings"
      intro={
        open > 0
          ? `${open} of ${q.data.settings.length} still open. Each question blocks only what it says until Finance answers it.`
          : "Every question is answered."
      }
    >
      <div className="divide-y divide-[#e7e7e4] rounded-md border border-rule bg-white">
        {q.data.settings.map((s) => (
          <SettingRowEditor key={s.key} s={s} />
        ))}
      </div>
    </Section>
  );
}

function SettingRowEditor({ s }: { s: SettingRow }) {
  const qc = useQueryClient();
  const initial = valueText(s.value);
  const [value, setValue] = useState(initial);
  const [msg, setMsg] = useState<{ tone: "ok" | "risk"; text: string } | null>(null);
  useEffect(() => setValue(initial), [initial]);

  const save = useMutation({
    mutationFn: () =>
      api(`/commissions/plan-settings/${s.key}`, {
        method: "PUT",
        body: JSON.stringify({ value: value.trim() === "" ? null : value.trim() }),
      }),
    onSuccess: () => {
      setMsg({ tone: "ok", text: "Saved — this answer now applies." });
      void qc.invalidateQueries({ queryKey: ["commissions"] });
    },
    onError: (e) => setMsg({ tone: "risk", text: errReason(e) }),
  });

  const set = isSet(s.value);
  const dirty = value !== initial;
  const input = s.input ?? (s.choices?.length ? "choice" : "text");
  const fieldId = `setting-${s.key}`;
  const chosenLabel = s.choices?.find((c) => c.value === initial)?.label;

  return (
    <div className="grid gap-4 p-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div>
        <div className="mb-1.5">{set ? <Pill tone="active">Set</Pill> : <Pill tone="warn">Not set</Pill>}</div>
        <label htmlFor={input === "choice" ? undefined : fieldId} className="block text-[16px] text-ink" id={`${fieldId}-label`}>
          {s.label}
        </label>
        <p className="mt-1 text-[14px] text-muted">
          {set ? "Controls" : "Blocks until set"}: {s.blocks}
        </p>
        {set && input === "choice" && chosenLabel ? (
          <p className="mt-1 text-[14px] text-ink">Current answer: {chosenLabel}</p>
        ) : null}
      </div>
      <div className="flex flex-col gap-2">
        {input === "date" ? (
          <>
            <input
              id={fieldId}
              type="date"
              className={`${INPUT} max-w-[240px]`}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setMsg(null);
              }}
            />
            {s.key === "payout_calendar_anchor" && (
              <p className="text-[13px] text-muted">
                Pick the first day of any payout period{s.example ? ` (e.g. ${s.example})` : ""}. Once a batch exists, the calendar can't move.
              </p>
            )}
          </>
        ) : input === "choice" ? (
          <div role="radiogroup" aria-labelledby={`${fieldId}-label`} className="flex flex-col gap-2">
            {(s.choices ?? []).map((c) => (
              <label key={c.value} className="flex cursor-pointer items-center gap-3 text-[15px] text-ink">
                <input
                  type="radio"
                  name={fieldId}
                  className="h-4 w-4"
                  checked={value === c.value}
                  onChange={() => {
                    setValue(c.value);
                    setMsg(null);
                  }}
                />
                {c.label}
              </label>
            ))}
          </div>
        ) : (
          <textarea
            id={fieldId}
            className={TEXTAREA}
            rows={3}
            value={value}
            placeholder={s.example ? `e.g. ${s.example}` : undefined}
            onChange={(e) => {
              setValue(e.target.value);
              setMsg(null);
            }}
          />
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
          {msg && (
            <span role="status" className={`text-[15px] ${msg.tone === "ok" ? "text-ok" : "text-risk"}`}>
              {msg.text}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
